export const PRICING_TIER_OPTIONS = [
  {
    pricingType: 'DAILY',
    label: '1 Day',
    rangeLabel: '1 - 6 days',
    packageDays: 1,
    perDay: true
  },
  {
    pricingType: 'WEEKLY',
    label: '1 Week',
    rangeLabel: '7 - 13 days',
    packageDays: 7,
    perDay: false
  },
  {
    pricingType: 'TWO_WEEKS',
    label: '2 Weeks',
    rangeLabel: '14 - 29 days',
    packageDays: 14,
    perDay: false
  },
  {
    pricingType: 'MONTHLY',
    label: '1 Month',
    rangeLabel: '30+ days',
    packageDays: 30,
    perDay: false
  }
];

const DEFAULT_DAILY_FALLBACK = {
  pricingType: 'DAILY',
  label: '1 Day',
  rangeLabel: '1 - 6 days',
  minDays: 1,
  maxDays: 6,
  packageDays: 1,
  perDay: true
};

export const normalizePricingConfigurations = (pricingConfigurations = []) => {
  const order = PRICING_TIER_OPTIONS.map((item) => item.pricingType);
  return [...pricingConfigurations]
    .map((item) => {
      const meta = PRICING_TIER_OPTIONS.find((option) => option.pricingType === item.pricingType) || DEFAULT_DAILY_FALLBACK;
      return {
        ...meta,
        ...item
      };
    })
    .sort((a, b) => order.indexOf(a.pricingType) - order.indexOf(b.pricingType));
};

export const getDefaultDailyPrice = (pricingConfigurations = [], fallbackPrice = 0) => {
  const dailyConfig = normalizePricingConfigurations(pricingConfigurations)
    .find((item) => item.pricingType === 'DAILY');

  return dailyConfig?.amount || fallbackPrice || 0;
};

export const resolveAppliedPricing = (pricingConfigurations = [], rentalDays = 0, fallbackDailyPrice = 0) => {
  const normalizedConfigurations = normalizePricingConfigurations(pricingConfigurations);
  const configurationMap = new Map(normalizedConfigurations.map((item) => [item.pricingType, item]));

  if (!rentalDays || rentalDays <= 0) {
    return {
      appliedConfig: normalizedConfigurations[0] || { ...DEFAULT_DAILY_FALLBACK, amount: fallbackDailyPrice || 0 },
      basePrice: 0,
      breakdown: []
    };
  }

  const dailyConfig = configurationMap.get('DAILY') || { ...DEFAULT_DAILY_FALLBACK, amount: fallbackDailyPrice || 0 };
  const dailyAmount = Number(dailyConfig.amount || fallbackDailyPrice || 0);
  const breakdown = [];
  let remainingDays = rentalDays;

  if (rentalDays >= 7) {
    ['MONTHLY', 'TWO_WEEKS', 'WEEKLY'].forEach((pricingType) => {
      const config = configurationMap.get(pricingType);
      const packageDays = Number(config?.packageDays || 0);
      const unitAmount = Number(config?.amount || 0);

      if (!config || !packageDays || unitAmount <= 0) {
        return;
      }

      const quantity = Math.floor(remainingDays / packageDays);
      if (quantity <= 0) {
        return;
      }

      breakdown.push({
        pricingType: config.pricingType,
        label: config.label,
        perDay: false,
        quantity,
        daysCovered: quantity * packageDays,
        unitAmount,
        totalAmount: unitAmount * quantity,
        description: `${config.label} x ${quantity}`
      });

      remainingDays -= quantity * packageDays;
    });
  }

  if (remainingDays > 0) {
    breakdown.push({
      pricingType: 'DAILY',
      label: dailyConfig.label,
      perDay: true,
      quantity: remainingDays,
      daysCovered: remainingDays,
      unitAmount: dailyAmount,
      totalAmount: dailyAmount * remainingDays,
      description: `${dailyConfig.label} x ${remainingDays}`
    });
  }

  if (!breakdown.length) {
    breakdown.push({
      pricingType: 'DAILY',
      label: dailyConfig.label,
      perDay: true,
      quantity: rentalDays,
      daysCovered: rentalDays,
      unitAmount: dailyAmount,
      totalAmount: dailyAmount * rentalDays,
      description: `${dailyConfig.label} x ${rentalDays}`
    });
  }

  const basePrice = breakdown.reduce((sum, item) => sum + Number(item.totalAmount || 0), 0);
  const appliedConfig = breakdown.length === 1
    ? {
        ...(configurationMap.get(breakdown[0].pricingType) || dailyConfig),
        amount: breakdown[0].unitAmount
      }
    : {
        pricingType: 'COMBINED',
        label: 'Combined Pricing',
        rangeLabel: `${rentalDays} days`,
        perDay: false,
        amount: basePrice
      };

  return {
    appliedConfig,
    basePrice,
    breakdown
  };
};
